import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronRight, Download } from 'lucide-react';
const COURSES: Record<string, any> = {
  fullstack: {
    title: 'FullStack Development',
    tagline: 'From prototype to production — build resilient web apps',
    overview: `A hands-on, project-based training covering modern frontend, backend and DevOps workflows. Trainees ship production-quality features and learn observability & testing practices used in real teams.`,
    curriculum: [
      'Modern React with TypeScript',
      'State management & performance',
      'Node.js/Express APIs & authentication',
      'Databases: Postgres & MongoDB',
      'CI/CD, Docker & deployment',
      'Testing & observability'
    ],
    downloads: [
      { name: 'FullStack Syllabus (PDF)', file: '/training/fullstack/syllabus.pdf' },
      { name: 'Sample Project (Repo)', file: '/training/fullstack/sample-project.zip' },
      { name: 'Intro Video', file: '/training/fullstack/intro.mp4' }
    ]
  },
  uiux: {
    title: 'UI / UX Design',
    tagline: 'Design systems, research and prototypes that convert',
    overview: `Learn to run rapid research, design high-fidelity prototypes and ship accessible design systems with developer-friendly handoffs.`,
    curriculum: [
      'User research & persona mapping',
      'Interaction design & prototyping (Figma)',
      'Design systems & tokens',
      'Accessibility & performance',
      'Design handoff & collaboration'
    ],
    downloads: [
      { name: 'UI/UX Syllabus (PDF)', file: '/training/uiux/syllabus.pdf' },
      { name: 'Figma Starter Kit', file: '/training/uiux/figma-kit.zip' },
      { name: 'Intro Video', file: '/training/uiux/intro.mp4' }
    ]
  },
  backend: {
    title: 'Backend Development',
    tagline: 'Scalable APIs and resilient systems',
    overview: `Build secure, maintainable and observable backend systems using modern patterns and best practices. Emphasis on APIs, security and operational excellence.`,
    curriculum: [
      'API design & versioning',
      'Datastore modelling & migrations',
      'Authentication & authorization',
      'Caching & queueing patterns',
      'Monitoring, testing & reliability'
    ],
    downloads: [
  { name: 'Backend Syllabus (PDF)', file: '/training/backend/syllabus.pdf' },
  { name: 'Workshop Exercises', file: '/training/backend/exercises.zip' },
  { name: 'Intro Video', file: '/training/backend/intro.mp4' }
    ]
  }
};

const TrainingDetail: React.FC = () => {
  const { courseId } = useParams();
  const course = courseId ? COURSES[courseId] : null;

  const location = useLocation();
  const [prefillCourse, setPrefillCourse] = useState(course ? course.title : '');

  useEffect(() => {
    const stateCourse = (location.state as any)?.courseTitle;
    if (stateCourse) setPrefillCourse(stateCourse);
  }, [location.state]);

  const base = (process.env.REACT_APP_BACKEND_URL || '/backend').replace(/\/+$/, '');

  if (!course) {
    return (
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold mb-4">Course not found</h2>
        <p className="text-sm text-slate-600">We couldn't find that training. Return to the training list.</p>
        <div className="mt-6">
          <Link to="/trainings" className="text-[#002a5c] font-semibold inline-flex items-center gap-2">Back to trainings <ChevronRight size={16} /></Link>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-4xl mx-auto px-4 py-12">
  <header className="mb-6 bg-gradient-to-r from-[#002a5c] via-[#002a5c] to-[#002a5c] p-6 rounded-2xl text-white">
        <h1 className="text-3xl font-extrabold">{course.title}</h1>
        <p className="text-sm text-white/90 mt-2">{course.tagline}</p>
      </header>

      <section className="mb-8 grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2">
          <p className="text-slate-800 mb-4">{course.overview}</p>

          <h4 className="font-semibold mb-2">What you'll learn</h4>
          <ul className="list-disc pl-5 text-slate-800 space-y-1 mb-6">
            {course.curriculum.map((c: string) => (<li key={c}>{c}</li>))}
          </ul>

          <h4 className="font-semibold mb-2">Who should attend</h4>
          <p className="text-slate-800 mb-4">Developers, designers and engineering managers who want practical, applicable skills they can use immediately in production.</p>

          <div className="mt-6 flex gap-3">
            <a href="#contact" className="px-4 py-2 bg-[#002a5c] text-white rounded-xl font-semibold">Request this training</a>
            <a href="tel:9849231222" className="px-4 py-2 border border-slate-200 rounded-xl">Call to enquire</a>
          </div>
        </div>

        <aside className="p-4 border border-slate-200 rounded-2xl bg-white">
          <h5 className="font-semibold mb-3">Downloads</h5>
          <ul className="space-y-3">
            {course.downloads.map((d: any) => (
              <li key={d.file}>
                <a href={`${base}/download.php?file=${encodeURIComponent(d.file)}`} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{d.name}</div>
                    <div className="text-[11px] text-slate-600">Download</div>
                  </div>
                  <Download size={18} className="text-slate-600" />
                </a>
              </li>
            ))}
          </ul>
        </aside>
      </section>

  <section id="contact" className="bg-gradient-to-r from-[#002a5c] via-[#002a5c] to-[#002a5c] rounded-2xl p-6">
        <div className="bg-white/95 p-6 rounded-xl shadow-sm border border-slate-100">
          <h4 className="text-lg font-semibold mb-2">Request a tailored proposal</h4>
          <p className="text-sm text-slate-700 mb-4">Tell us your goals and team size and we'll send a tailored syllabus and pricing.</p>
          <form id="proposalForm" className="mt-4" onSubmit={async (e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget as HTMLFormElement);
          fd.append('course', prefillCourse || '');
          try {
            const base = (process.env.REACT_APP_BACKEND_URL || '/backend').replace(/\/+$/, '');
            const res = await fetch(`${base}/api.php?action=save_proposal`, { method: 'POST', body: fd });
            const data = await res.json();
            if (res.ok) {
              toast.success('Proposal request sent. We will contact you soon.');
              (e.currentTarget as HTMLFormElement).reset();
            } else {
              toast.error(data.error || 'Failed to send request');
            }
          } catch (err) {
            toast.error('Network error');
          }
        }}>
          <div className="grid md:grid-cols-3 gap-3">
            <label className="flex flex-col">
              <span className="text-[11px] font-semibold text-slate-600 mb-1">Company / Team</span>
              <input name="company" placeholder="Company / Team" className="p-3 rounded-xl border border-slate-200" />
            </label>
            <label className="flex flex-col">
              <span className="text-[11px] font-semibold text-slate-600 mb-1">Contact name</span>
              <input name="contact" placeholder="Contact name" className="p-3 rounded-xl border border-slate-200" />
            </label>
            <label className="flex flex-col">
              <span className="text-[11px] font-semibold text-slate-600 mb-1">Phone / Email</span>
              <input name="contact_info" placeholder="Phone / Email" className="p-3 rounded-xl border border-slate-200" />
            </label>
          </div>
          <div className="mt-4 flex gap-3 items-center">
            <label className="flex-1">
              <span className="text-[11px] font-semibold text-slate-600 mb-1 block">Selected Course</span>
              <input readOnly name="course_prefill" value={prefillCourse} className="w-full p-3 rounded-xl border border-slate-200 bg-white/60" />
            </label>
            <div className="flex items-center gap-3">
              <button type="submit" className="px-4 py-2 bg-[#002a5c] text-white rounded-xl font-semibold">Request Proposal</button>
              <Link to="/contact" className="px-4 py-2 border border-slate-300 rounded-xl text-sm">Contact Us</Link>
            </div>
          </div>
        </form>
        </div>
      </section>
    </main>
  );
};

export default TrainingDetail;
